import React, { useContext } from 'react';
import {
  arrayOf,
  string,
  number
} from 'prop-types';
import {
  extend,
  useDataModel,
  QueryProvider
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { Price } from '@thd-olt-component-react/price';
import {
  Col,
  Row,
  Image,
  Rating,
  Placeholder,
  Carousel
} from '@thd-olt-component-react/core-ui';
import { lifeStyleProductsDataModel } from './LifeStyleDataModel';
import {
  triggerAnalytics,
  getProductInfo,
  getFilteredProduct
} from '../../helpers';
import './styles/details.scss';
import './styles/overrides.scss';

const LifeStyleDetails = ({ omsIDs, imageUrl: lifeStyleImage }) => {

  const { channel } = useContext(ExperienceContext);
  const store = useStore();
  const { storeId, zipCode } = store;

  const onProductLinkClick = (itemId) => {
    triggerAnalytics('product-hotspots.product-link-click', { sku: itemId });
  };

  const { data, loading, error } = useDataModel('searchModel', {
    variables: {
      itemIds: omsIDs,
      storeId
    }
  });

  if (error && !data) {
    return null;
  }

  const { searchModel } = data || {};
  const { products } = searchModel || [];
  const isMobile = channel === 'mobile';

  if (!data || loading) {
    return (
      <>
        <Row className="details__main-row details__main-row--loading show-loading-animation">
          <Col className="details__left-column" fallback="6" nopadding>
            <Placeholder
              className="details__loader--left"
              type="rect"
              showLoadingAnimation
            />
          </Col>
          <Col className="details__right-column" fallback="6">
            <div className="details__loader--right">
              <Placeholder
                type="text"
                className="placeholder-text-row placeholder-text-row--first"
                showLoadingAnimation
              />
              <Placeholder
                type="text"
                className="placeholder-text-row"
                showLoadingAnimation
              />
              <Placeholder
                type="text"
                className="placeholder-text-row"
                showLoadingAnimation
              />
              <Placeholder
                type="text"
                className="placeholder-text-row"
                showLoadingAnimation
              />
              <Placeholder
                type="text"
                className="placeholder-text-row"
                showLoadingAnimation
              />
              <Placeholder
                type="text"
                className="placeholder-text-row"
                showLoadingAnimation
              />
            </div>
          </Col>
        </Row>
      </>
    );
  }
  if (!data || !products?.length) {
    return (<div data-testid="no-data"> This item is currently unavailable </div>);
  }

  const renderDetails = (skuContainer = {}, key = '') => {
    const {
      averageRating,
      brandName,
      imageUrl,
      productLabel,
      productUrl,
      totalReviews,
      itemId
    } = getProductInfo(skuContainer);

    return (
      <a
        href={productUrl}
        onClick={() => onProductLinkClick(itemId)}
        rel="noreferrer"
        target={isMobile ? '_self' : '_blank'}
        data-testid="tooltip-product"
        key={key}
      >
        <Row className="details__main-row">
          <Col className="details__left-column" fallback="6" nopadding>
            <Image
              alt="Product"
              className="details__product-image"
              src={imageUrl?.replace(/<SIZE>/g, '300')}
              width="100"
              height="50"
            />
          </Col>
          <Col className="details__right-column" fallback="6">
            <Row>
              <span className="details__product-label">
                {productLabel}
              </span>
              {brandName && (
                <span className="details__brand">
                  by&nbsp;
                  <span className="details__brand--bold">
                    {brandName}
                  </span>
                </span>
              )}
            </Row>
            {totalReviews > 0 && (
              <Row className="details__rating">
                <Rating className="details__rating-stars" value={Number(averageRating)} />
                <span>({totalReviews})</span>
              </Row>
            )}
            <Row>
              <Price itemId={`${ itemId }`} storeId={storeId} large={false} />
            </Row>
          </Col>
        </Row>
      </a>
    );
  };
  if (!products?.length) {
    return null;
  }
  const filteredProducts = getFilteredProduct(products, lifeStyleImage);
  return (
    <QueryProvider
      dataSource="searchNav"
      defaultVariables={{ storeId, zipCode, skipKPF: true, skipSubscribeAndSave: true }}
      cacheKey="details"
    >
      <div className="details__container">
        <Carousel
          breakpoints={{
            sm: {
              slidesPerView: 1,
              slidesPerGroup: 1
            },
            md: {
              slidesPerView: 1,
              slidesPerGroup: 1
            },
            lg: {
              slidesPerView: 1,
              slidesPerGroup: 1
            },
            xl: {
              slidesPerView: 1,
              slidesPerGroup: 1
            }
          }}
          peek={false}
          navigation
          slidesPerGroup={1}
          slidesPerView={1}
        >
          {(filteredProducts || []).map((product, index) => renderDetails(product, index))}
        </Carousel>
      </div>
    </QueryProvider>
  );
};
LifeStyleDetails.dataModel = extend({}, lifeStyleProductsDataModel);

LifeStyleDetails.propTypes = {
  omsIDs: arrayOf(string).isRequired,
  imageUrl: string
};

LifeStyleDetails.defaultProps = {
  imageUrl: ''
};

export { LifeStyleDetails };
